// Queue model helpers (contract D.2). Pure functions, no React.
//
// QueueItem = { qid, bandKey, bandName, logo, tapeId, sid|null, dateKey, iso,
//               venue, location, no, title, length, url, trackIdx,
//               chainId|null, groupLabel|null, secs|undefined }
//   (secs: the track's seconds when the server sent them; segment items only)
// Queue     = { gen, items, index, title,
//               source: { kind: 'show'|'matches'|'radio'|'song'|'surprise'|'link',
//                         band, params, cursor, exhausted, radio, pages } }
//   (pages: pages fetched so far, 1 for the page the queue started with)
//
// App.js owns the queue state. The invariants that keep the gapless engine
// safe (D.2 1-6) live there; the helpers here only build and read items:
// - items are never rebuilt once created (appendItems concatenates),
// - an append for an older `gen` is dropped,
// - qids are unique within a queue (batchNo increments per append).

import { bandByKey } from './bands';
import { streamUrl } from './api';

// 'YYYY-MM-DDT00:00:00Z' or 'YYYY_MM_DD' -> 'YYYY-MM-DD'
export const isoFromDateKey = (dateKey) => String(dateKey || '').substring(0, 10).replace(/_/g, '-');

// Browse and ?set= links. `band` is a bands.js entry, `show` the canonical
// browse show ({ dateKey, venue, location, ... }), `set` the adaptSet result.
// `url` is copied byte-for-byte from adaptSet so selectTape's warm-start
// (srcsRef[sb] === url) still hits on the first play.
export const itemsFromSet = (band, show, tapeId, set) => {
  const tracks = (set && set.tracks) || [];
  const s = show || {};
  return tracks.map((t, trackIdx) => ({
    qid: 'set:' + tapeId + ':' + trackIdx,
    bandKey: band.key,
    bandName: band.name,
    logo: band.logo,
    tapeId,
    sid: s.sid || null,
    dateKey: s.dateKey,
    iso: isoFromDateKey(s.dateKey),
    venue: s.venue,
    location: s.location,
    no: t.no,
    title: t.title,
    length: t.length,
    url: t.url,
    trackIdx,
    chainId: null,
    groupLabel: null,
  }));
};

// Segments (find/matches, radio, surprise, segment) -> QueueItems. The band is
// resolved per segment, so cross-band plays (SongPage "Other bands") get the
// right name and logo. Segments for an unknown band, and tracks without a
// file, are skipped.
export const segmentsToItems = (segments, batchNo) => {
  const out = [];
  (segments || []).forEach((seg) => {
    if (!seg || !Array.isArray(seg.tracks)) return;
    const band = bandByKey(seg.bandKey);
    if (!band) return;
    seg.tracks.forEach((t) => {
      if (!t || !t.file) return;
      out.push({
        qid: batchNo + ':' + seg.segId + ':' + t.trackIdx,
        bandKey: band.key,
        bandName: band.name,
        logo: band.logo,
        tapeId: seg.tapeId,
        sid: seg.sid || null,
        dateKey: seg.dateKey,
        iso: seg.iso || isoFromDateKey(seg.dateKey),
        venue: seg.venue,
        location: seg.location,
        no: t.no,
        title: t.title,
        length: t.length,
        url: streamUrl(seg.bandKey, t.file),
        trackIdx: t.trackIdx,
        chainId: seg.chainId || null,
        groupLabel: seg.label || null,
        secs: Number.isFinite(t.secs) && t.secs > 0 ? t.secs : undefined,
      });
    });
  });
  return out;
};

export const makeSource = (kind, band, extra = {}) => ({
  kind,
  band,
  params: null,
  cursor: null,
  exhausted: kind !== 'matches' && kind !== 'radio' && kind !== 'song',
  radio: null,
  pages: 1,
  ...extra,
});

export const makeQueue = (gen, items, title, source, index = 0) => ({
  gen,
  items: items || [],
  index,
  title: title || '',
  source: source || makeSource('show', null),
});

// setQueue((q) => appendItems(q, gen, newItems, patch)): the D.2 append rule.
// Existing item objects are kept as-is; `patch` merges into `source`
// (for example { cursor, exhausted }).
export const appendItems = (q, gen, newItems, sourcePatch) => {
  if (!q || q.gen !== gen) return q;
  const items = newItems && newItems.length ? q.items.concat(newItems) : q.items;
  const source = sourcePatch ? { ...q.source, ...sourcePatch } : q.source;
  if (items === q.items && source === q.source) return q;
  return { ...q, items, source };
};

const FETCHABLE = { matches: true, radio: true, song: true };

// 'mm:ss' or 'h:mm:ss' -> seconds, or null.
export const secsOfLength = (len) => {
  const m = /^(?:(\d+):)?(\d{1,3}):(\d{2})$/.exec(String(len || '').trim());
  if (!m) return null;
  const s = (Number(m[1]) || 0) * 3600 + Number(m[2]) * 60 + Number(m[3]);
  return s > 0 ? s : null;
};

// A queue item's seconds, or null when unknown.
export const itemSecs = (item) => {
  if (!item) return null;
  if (Number.isFinite(item.secs) && item.secs > 0) return item.secs;
  return secsOfLength(item.length);
};

// Seconds of music in items[from..], and how many of those items had a length.
export const queuedSecs = (items, from = 0) => {
  let secs = 0;
  let known = 0;
  const list = items || [];
  for (let i = Math.max(0, from); i < list.length; i++) {
    const s = itemSecs(list[i]);
    if (s) {
      secs += s;
      known++;
    }
  }
  return { secs, known, count: Math.max(0, list.length - Math.max(0, from)) };
};

// Fetch-more thresholds. A queue refills when less than REFILL_SECS of music
// is left after the current item. A new queue whose first pages hold less
// than FILL_SECS in all keeps fetching (at most FILL_PAGES pages), so a
// station starts with 2+ hours queued even when a page comes back short.
// Without track lengths (fewer than half the items after the current one
// have one) the old rule applies: 3 or fewer items left.
export const REFILL_SECS = 45 * 60;
export const FILL_SECS = 120 * 60;
export const FILL_PAGES = 4;
const COUNT_FALLBACK = 3;

// Fetch-more trigger (D.2, by duration). The source can fetch and is not
// exhausted; the caller also checks that no fetch is in flight.
export const needsMore = (q) => {
  if (!(q && q.source && FETCHABLE[q.source.kind] && !q.source.exhausted)) return false;
  const ahead = queuedSecs(q.items, q.index + 1);
  if (ahead.count > 0 && ahead.known * 2 < ahead.count) return q.items.length - q.index <= COUNT_FALLBACK;
  if (ahead.secs < REFILL_SECS) return true;
  if ((q.source.pages || 1) < FILL_PAGES && queuedSecs(q.items, 0).secs < FILL_SECS) return true;
  return false;
};

// '2 h 34 min', '3 h', '45 min', '1 min'; '' for nothing.
export const fmtQueued = (secs) => {
  const m = Math.round((Number(secs) || 0) / 60);
  if (m <= 0) return secs > 0 ? '1 min' : '';
  const h = Math.floor(m / 60);
  if (!h) return m + ' min';
  return m % 60 ? h + ' h ' + (m % 60) + ' min' : h + ' h';
};

// Thumbs-down skip: index of the first item after `index` whose chainId
// differs from `chainId`, or -1 when the rest of the queue is that chain.
export const nextOtherChain = (items, index, chainId) => {
  if (!chainId) return index + 1 < (items || []).length ? index + 1 : -1;
  for (let i = index + 1; i < items.length; i++) {
    if (items[i].chainId !== chainId) return i;
  }
  return -1;
};

// Show key for grouping: sid when known, else band + dateKey (+ tape).
export const showKeyOf = (item) =>
  item.sid ? item.bandKey + '|' + item.sid : item.bandKey + '|' + item.dateKey + '|' + item.tapeId;

// Queue panel grouping: consecutive items of one show become one group.
// -> [{ key, bandKey, bandName, sid, dateKey, iso, venue, location, tapeId,
//       first: index of the first item, items: [{ item, index }] }]
// `key` is unique within the result (a repeated show later in the queue gets
// a suffix), so it is safe as a React key.
export const groupItems = (items) => {
  const groups = [];
  const seen = {};
  (items || []).forEach((item, index) => {
    const k = showKeyOf(item);
    const last = groups[groups.length - 1];
    if (last && last.showKey === k) {
      last.items.push({ item, index });
      return;
    }
    seen[k] = (seen[k] || 0) + 1;
    groups.push({
      key: seen[k] > 1 ? k + '#' + seen[k] : k,
      showKey: k,
      bandKey: item.bandKey,
      bandName: item.bandName,
      sid: item.sid,
      dateKey: item.dateKey,
      iso: item.iso,
      venue: item.venue,
      location: item.location,
      tapeId: item.tapeId,
      first: index,
      items: [{ item, index }],
    });
  });
  return groups;
};

// Distinct sids in order of first appearance (for pushRecentSid bookkeeping).
export const sidsOf = (items) => {
  const out = [];
  const seen = {};
  (items || []).forEach((it) => {
    if (it.sid && !seen[it.sid]) {
      seen[it.sid] = true;
      out.push(it.sid);
    }
  });
  return out;
};
